import { sanitizeDecimalInput } from './decimalInput';

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Allows formatted values such as `$1,234.56` to round-trip on focus. */
export const parseCurrency = (raw: string): number => {
  const parsed = Number(sanitizeDecimalInput(raw));

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatCurrency = (value: number): string => CURRENCY_FORMATTER.format(value);

const AMOUNT_FORMATTER = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** For fields that already carry a `$` affix, where `formatCurrency` would stack a second symbol. */
export const formatAmount = (value: number): string => AMOUNT_FORMATTER.format(value);

const PLAIN_AMOUNT_FORMATTER = new Intl.NumberFormat('en-US', { useGrouping: false, maximumFractionDigits: 2 });

/**
 * Bare digits for a field mid-edit. Not `String()`, which writes 1e21 and up as `1e+22`, and
 * sanitizing that for parsing strips it to `122`.
 */
export const formatPlainAmount = (value: number): string => PLAIN_AMOUNT_FORMATTER.format(value);

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 1,
});

/**
 * Below this an amount is printed in full, so a decimal on it reads as cents and has to carry both
 * of them; above it the decimal is a fraction of the K/M/B suffix, where a padded `$26.10K` misleads.
 */
const ABBREVIATION_THRESHOLD = 1000;

const EXACT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  trailingZeroDisplay: 'stripIfInteger',
});

/** For axis ticks and headlines, where `formatCurrency`'s fixed two decimals runs to 18 characters in the billions. */
export const formatCompactCurrency = (value: number): string =>
  Math.abs(value) < ABBREVIATION_THRESHOLD ? EXACT_FORMATTER.format(value) : COMPACT_FORMATTER.format(value);

/** Avoid floating-point rounding errors in money comparisons and divisions. */
export const toCents = (value: number): number => Math.round(value * 100);

export const fromCents = (cents: number): number => cents / 100;

/** Truncates, so a projected balance is never shown as more money than the member would hold. */
export const floorCents = (cents: number): number => Math.floor(cents);
